/**
 * Utility to convert legacy tournament JSON format to Strapi-compatible sections format
 * This allows backward compatibility while supporting the new dynamic rendering system
 */

/**
 * Converts legacy tournament content format to new sections-based format
 * @param {Object} tournamentContent - Legacy tournament content object
 * @returns {Array} - Array of sections with type and props
 */
export const convertLegacyToSections = (tournamentContent) => {
  if (!tournamentContent) return [];

  const sections = [];
  const { content, guaranteed, guaranteedDate, videoSection, imageSection, prizePool, imageTextWrapper } = tournamentContent;

  // If sections already exist, return them (new format)
  if (content?.sections) {
    return content.sections;
  }

  // Convert legacy format to sections
  
  // Coverage Banner (above content)
  if (content?.coverageBanner?.link && !content?.coverageBanner?.below) {
    sections.push({
      type: "CoverageAndResultsBanner",
      props: {
        label: content.coverageBanner.label,
        resultsLink: content.coverageBanner.link
      }
    });
  }

  // Image
  if (content?.image) {
    sections.push({
      type: "ResponsiveImage",
      props: {
        src: content.image,
        alt: tournamentContent?.hero?.imageAlt
      }
    });
  }

  // Three Column Images
  if (content?.threeColumnImages?.images) {
    sections.push({
      type: "ThreeColumnImages",
      props: {
        images: content.threeColumnImages.images
      }
    });
  }

  // Guaranteed sections
  if (guaranteed?.over) {
    sections.push({
      type: "ContentSection",
      props: {
        title: guaranteed.over,
        middleText: guaranteed.overPrizePool,
        paragraphText: guaranteed.text,
        isAmount: true
      }
    });
  }

  if (guaranteed?.mainEvent) {
    sections.push({
      type: "ContentSection",
      props: {
        title: guaranteed.mainEvent,
        middleText: guaranteed.mainEventPrizePool,
        paragraphText: guaranteed.text,
        isAmount: true
      }
    });
  }

  // Guaranteed Date
  if (guaranteedDate) {
    sections.push({
      type: "ContentSection",
      props: {
        title: guaranteedDate.weekDay,
        middleText: guaranteedDate.date,
        paragraphText: guaranteedDate.time,
        isAmount: false
      }
    });
  }

  // Title and Text
  if (content?.title) {
    sections.push({
      type: "TitleSection",
      props: {
        // Note: 'dark' prop controls text color on TitleSection component
        // When variant is "dark" (dark background), we want dark: false (light text)
        // When variant is "light" (light background), we want dark: true (dark text)
        dark: content?.variant !== "dark",
        as: "h2",
        title: content.title,
        text: content.text
      }
    });
  } else if (content?.text) {
    sections.push({
      type: "TextContent",
      props: {
        content: content.text,
        variant: content?.variant
      }
    });
  }

  // Hotel Info
  if (content?.hotelInfo) {
    content.hotelInfo.forEach(hotel => {
      sections.push({
        type: "HotelCard",
        props: hotel
      });
    });
  }

  // Flight/Travel
  if (content?.flight) {
    sections.push({
      type: "TravelDiscount",
      props: {
        flight: content.flight
      }
    });
  }

  // Grid
  if (content?.grid) {
    sections.push({
      type: "GridContent",
      props: {
        grid: content.grid
      }
    });
  }

  // Event Content
  if (tournamentContent?.eventContent) {
    sections.push({
      type: "EventContent",
      props: {
        data: tournamentContent.eventContent
      }
    });
  }

  // Highlighted List
  if (content?.highlightedList) {
    sections.push({
      type: "HighlightedList",
      props: {
        highlightedList: content.highlightedList
      }
    });
  }

  // Dynamic Table Data
  if (content?.dynamicTableData) {
    sections.push({
      type: "DynamicTable",
      props: {
        tableData: content.dynamicTableData
      }
    });
  }

  // Text Two
  if (content?.textTwo) {
    sections.push({
      type: "TextContent",
      props: {
        content: content.textTwo,
        variant: content?.variant
      }
    });
  }

  // Dynamic Table Data Two
  if (content?.dynamicTableDataTwo) {
    sections.push({
      type: "DynamicTable",
      props: {
        tableData: content.dynamicTableDataTwo
      }
    });
  }

  // Centered Text
  if (content?.centeredText) {
    sections.push({
      type: "CenteredTextContent",
      props: {
        content: content.centeredText
      }
    });
  }

  // Highlighted List Two
  if (content?.highlightedListTwo) {
    sections.push({
      type: "HighlightedList",
      props: {
        highlightedList: content.highlightedListTwo
      }
    });
  }

  // Text Three
  if (content?.textThree) {
    sections.push({
      type: "TextContent",
      props: {
        content: content.textThree,
        variant: content?.variant
      }
    });
  }

  // Video Section
  if (videoSection) {
    sections.push({
      type: "VideoSection",
      props: {
        videoUrl: videoSection.link,
        textContent: videoSection.text
      }
    });
  }

  // Image Section
  if (imageSection) {
    sections.push({
      type: "ImageSection",
      props: {
        imageUrl: imageSection.image,
        imageAlt: imageSection.imageAlt,
        textContent: imageSection.text
      }
    });
  }

  // Small Text
  if (content?.smallText) {
    sections.push({
      type: "SmallTextContent",
      props: {
        content: content.smallText
      }
    });
  }

  // Dropdown Text
  if (content?.dropdownText) {
    sections.push({
      type: "DropDownText",
      props: {
        dropdownText: content.dropdownText
      }
    });
  }

  // Prize Pool
  if (prizePool) {
    sections.push({
      type: "PrizePoolTable",
      props: {
        prizePool: prizePool
      }
    });
  }

  // Image Text Wrapper
  if (imageTextWrapper) {
    sections.push({
      type: "ImageTextWrapper",
      props: {
        imageTextWrapper: imageTextWrapper
      }
    });
  }

  // Table Data Text
  if (content?.tableDataText) {
    sections.push({
      type: "TextContent",
      props: {
        content: content.tableDataText,
        variant: content?.variant
      }
    });
  }

  // Table Data
  if (content?.tableData) {
    sections.push({
      type: "SeatsGuaranteedTable",
      props: {
        tableData: content.tableData
      }
    });
  }

  // Table Data Text Two
  if (content?.tableDataTextTwo) {
    sections.push({
      type: "TextContent",
      props: {
        content: content.tableDataTextTwo,
        variant: content?.variant
      }
    });
  }

  // Table Data Two
  if (content?.tableDataTwo) {
    sections.push({
      type: "SeatsGuaranteedTable",
      props: {
        tableData: content.tableDataTwo
      }
    });
  }

  // Sectional Tabs
  if (content?.sectionalTabs) {
    sections.push({
      type: "SectionalTabs",
      props: {
        subTabs: content.sectionalTabs
      }
    });
  }

  // Coverage Banner (below content)
  if (content?.coverageBanner?.link && content?.coverageBanner?.below) {
    sections.push({
      type: "CoverageAndResultsBanner",
      props: {
        label: content.coverageBanner.label,
        resultsLink: content.coverageBanner.link
      }
    });
  }

  // Bottom Text
  if (content?.bottomText) {
    sections.push({
      type: "TextContent",
      props: {
        content: content.bottomText,
        variant: content?.variant
      }
    });
  }

  // Bottom Button
  if (content?.bottomButton) {
    sections.push({
      type: "Button",
      props: {
        bgColor: "var(--red)",
        color: "var(--white)",
        hoverBgColor: "var(--mutedred)",
        hoverColor: "var(--white)",
        href: content.bottomButton.link,
        label: content.bottomButton.label,
        width: "wide",
        size: "large",
        className: "mb-5"
      }
    });
  }

  return sections;
};

/**
 * Checks if the tournament content uses the new sections format
 * @param {Object} tournamentContent - Tournament content object
 * @returns {boolean} - True if using new format
 */
export const isNewFormat = (tournamentContent) => {
  return !!(tournamentContent?.content?.sections);
};
